import { MouseEvent, useCallback } from 'react'
import Tooltip from '@mui/material/Tooltip'
import { ITask } from '@common/interfaces/task'
import cn from 'classnames'
import TaskStatusGlyph from '../../../components/Tasks/TaskStatusGlyph'
import styles from './TaskRowStatusIcon.module.scss'

export type ITaskRowStatusIconProps = {
  task: ITask
  /** Task is in the completed-today section or otherwise completed */
  isComplete: boolean
  isRowSelected: boolean
  onMarkComplete?: (task: ITask) => void
  onUncomplete?: (task: ITask) => void
  /** 'sm' for a compact chip, such as a task on the calendar. */
  size?: 'sm' | 'md'
}

const TaskRowStatusIcon = ({
  task,
  isComplete,
  isRowSelected,
  onMarkComplete,
  onUncomplete,
  size = 'md',
}: ITaskRowStatusIconProps) => {
  const handleMarkComplete = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      onMarkComplete?.(task)
    },
    [onMarkComplete, task],
  )

  const handleUncomplete = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      onUncomplete?.(task)
    },
    [onUncomplete, task],
  )

  if (isComplete && onUncomplete) {
    return (
      <Tooltip title="Mark incomplete" enterNextDelay={300}>
        <button
          type="button"
          className={styles.hitTarget}
          onClick={handleUncomplete}
          aria-label="Mark incomplete"
        >
          <TaskStatusGlyph variant="complete" size={size} />
        </button>
      </Tooltip>
    )
  }

  if (isComplete) {
    return <TaskStatusGlyph variant="complete" size={size} label="Completed" />
  }

  if (onMarkComplete) {
    // Idle is silver-on-silver; hover swaps ring and glyph to jungle green (the
    // parent .hitTarget drives it); the completed branch above is solid green.
    return (
      <Tooltip title="Mark complete" enterNextDelay={300}>
        <button
          type="button"
          className={styles.hitTarget}
          onClick={handleMarkComplete}
          aria-label="Mark complete"
        >
          <TaskStatusGlyph
            variant="incomplete"
            size={size}
            className={cn(styles.incompleteRingActionable, {
              [styles.incompleteRingSelected]: isRowSelected,
            })}
          />
        </button>
      </Tooltip>
    )
  }

  return (
    <span className={styles.hitTargetStatic}>
      <TaskStatusGlyph
        variant="incomplete"
        size={size}
        label="Not completed"
        className={styles.incompleteRingStatic}
      />
    </span>
  )
}

export default TaskRowStatusIcon
